import { css } from "@emotion/css";
import { Button, Divider } from "@octopusdeploy/design-system-components";
import { text, themeTokens, borderRadius, space } from "@octopusdeploy/design-system-tokens";
import type { DeploymentFreezeTenantScopeDetail } from "@octopusdeploy/octopus-server-client";
import _ from "lodash";
import pluralize from "pluralize";
import * as React from "react";
import { useState } from "react";
import type { NamedItemWithLogoAndEnvironments } from "~/areas/projects/components/ProjectTenants/SelectEnvironments";
import { TenantNameCell } from "~/areas/tenants/components/DataTable/Cells/TenantNameCell";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { ProjectMultiSelect } from "~/components/MultiSelect/ProjectMultiSelect";
import NumberedPagingBar from "~/components/PagingBaseComponent/NumberedPagingBar";
import type { TenantProjectEnvironmentRowData } from "../DeploymentFreezeTenantScopeTable";
interface SelectProjectsProps {
    selectedTenants: DeploymentFreezeTenantScopeDetail[];
    getAvailableProjects: () => Promise<NamedItemWithLogoAndEnvironments[]>;
    updateSelectedProjects: (projectIds: string[]) => void;
    doBusyTask: DoBusyTask;
}
export default function SelectProjects({ selectedTenants, getAvailableProjects, updateSelectedProjects, doBusyTask }: SelectProjectsProps) {
    const [availableProjects, setAvailableProjects] = useState<NamedItemWithLogoAndEnvironments[] | null>(null);
    const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(_.uniq(selectedTenants.flatMap((pe) => pe.ProjectEnvironments.map((p) => p.Id))));
    useDoBusyTaskEffect(doBusyTask, async () => {
        setAvailableProjects(await getAvailableProjects());
        updateSelectedProjects(selectedProjectIds);
    }, []);
    const onProjectChange = (projectIds: string[]) => {
        setSelectedProjectIds(projectIds);
        updateSelectedProjects(projectIds);
    };
    if (availableProjects === null)
        return null;
    return (<div className={styles.container}>
            <div className={styles.panelContainer}>
                <SelectProjectsPanel availableProjects={availableProjects} selectedProjectIds={selectedProjectIds} onProjectChange={onProjectChange} selectedTenants={selectedTenants}/>
                <Divider />
                <ConnectionPreview items={selectedTenants}/>
            </div>
        </div>);
}
interface SelectProjectsPanelProps {
    selectedTenants: DeploymentFreezeTenantScopeDetail[];
    selectedProjectIds: string[];
    onProjectChange: (projectIds: string[]) => void;
    availableProjects: NamedItemWithLogoAndEnvironments[];
}
function SelectProjectsPanel({ selectedTenants, selectedProjectIds, onProjectChange, availableProjects }: SelectProjectsPanelProps) {
    const onAssignAll = () => {
        onProjectChange(availableProjects.map((e) => e.Id));
    };
    return (<>
            <div className={styles.panelHeadingContainer}>
                <div className={styles.panelHeading}>Select projects for {pluralize("tenants", selectedTenants.length, true)}</div>
            </div>
            <div className={styles.panelHeadingContainer}>
                <div>
                    <ProjectMultiSelect onChange={onProjectChange} value={selectedProjectIds} items={availableProjects}/>
                </div>
                <div>
                    <Button label={"Assign all available projects"} onClick={onAssignAll} importance={"tertiary"}/>
                </div>
            </div>
        </>);
}
interface ConnectionPreviewProps {
    items: DeploymentFreezeTenantScopeDetail[];
}
function ConnectionPreview({ items }: ConnectionPreviewProps) {
    const pageSize = 30;
    const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
    const [paginatedScopeItems, setPaginatedItems] = useState<DeploymentFreezeTenantScopeDetail[]>(items);
    React.useEffect(() => {
        const pagedItems = items.slice(pageSize * currentPageIndex, pageSize * (currentPageIndex + 1));
        setPaginatedItems(pagedItems);
    }, [items, currentPageIndex]);
    const mapToRowData = (items: DeploymentFreezeTenantScopeDetail[]): TenantProjectEnvironmentRowData[] => {
        return items.flatMap((item) => {
            if (!item.ProjectEnvironments?.length) {
                return [
                    {
                        Id: item.Id,
                        Name: item.Name,
                        LogoLink: item.LogoLink,
                        SpaceId: item.SpaceId,
                        Space: item.Space,
                        IsDisabled: item.IsDisabled,
                        RowKey: `${item.Id}`,
                        IsChild: false,
                        ProjectEnvironment: {
                            Id: item.Id,
                            Name: "No projects",
                            LogoLink: "",
                            SpaceId: item.SpaceId,
                            Environments: [],
                        },
                    },
                ];
            }
            return item.ProjectEnvironments.map((projectEnv, index) => ({
                Id: item.Id,
                Name: index === 0 ? item.Name : "",
                LogoLink: item.LogoLink,
                SpaceId: item.SpaceId,
                Space: item.Space,
                IsDisabled: item.IsDisabled,
                RowKey: `${item.Id}-${projectEnv.Id}-${index}`,
                IsChild: index !== 0,
                ProjectEnvironment: projectEnv,
            }));
        });
    };
    const rowDatas = mapToRowData(paginatedScopeItems);
    return (<>
            <div className={styles.previewTitle}>Connection preview</div>
            <div className={styles.tableContainer}>
                <GroupedDataTable data={rowDatas} columns={[
            {
                title: "Tenant",
                render: (item) => (item.IsChild ? null : <TenantNameCell tenantName={item.Name} tenantLogoLink={item.LogoLink} space={item.Space} isDisabled={item.IsDisabled}/>),
                width: "30%",
                isChild: (item) => item.IsChild,
            },
            {
                title: "Project",
                render: (item) => <TenantNameCell tenantName={item.ProjectEnvironment.Name} tenantLogoLink={item.ProjectEnvironment.LogoLink}/>,
                width: "30%",
            },
        ]} getRowKey={(data) => data.RowKey}/>
                <NumberedPagingBar totalItems={items.length} currentPageIndex={currentPageIndex} pageSize={pageSize} onPageSelected={(_, index) => setCurrentPageIndex(index)}/>
            </div>
        </>);
}
const styles = {
    container: css({
        width: "100%",
        height: "100%",
        padding: space[16],
        backgroundColor: themeTokens.color.background.secondary.default,
    }),
    panelContainer: css({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${themeTokens.color.border.primary}`,
        borderRadius: borderRadius.small,
        backgroundColor: themeTokens.color.background.primary.default,
        overflowY: "hidden",
    }),
    panelHeadingContainer: css({
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: "10rem",
        alignItems: "center",
        padding: space[16],
    }),
    panelHeading: css({
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
    }),
    previewTitle: css({
        padding: space[16],
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
    }),
    tableContainer: css({
        overflowY: "auto",
    }),
};
